import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { megaMenuCategories, services } from '../data/serviceCatalog';
import { ArrowRight, ChevronRight } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const ServicesPage = () => {
  useSEO({
    title: 'All Business Services — Utkarshudyam',
    description: 'Browse all Utkarshudyam services — company registration, GST, MSME, Startup India, ISO, FSSAI, IEC, trademarks, business loans and more.',
    canonical: typeof window !== 'undefined' ? window.location.origin + '/services' : undefined,
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-14 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0] relative overflow-hidden">
        <div className="absolute top-10 right-10 w-96 h-96 bg-[#FFB703] rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-[#FFB703] text-white px-4 py-1.5">Our Services</Badge>
            <h1 className="text-3xl md:text-5xl font-black text-white">Comprehensive Business Solutions</h1>
            <p className="text-lg md:text-xl text-gray-200 mt-4">End-to-End Registration, Compliance, Certification and Growth Services</p>
          </div>
        </div>
      </section>

      {/* Categories with services */}
      <section className="py-14 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-10">
            {megaMenuCategories.map((cat) => (
              <div key={cat.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-[#0A2463] via-[#FFB703] to-[#FB8500]" />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                    <div>
                      <div className="text-xs font-bold uppercase text-[#FB8500] tracking-widest">{cat.tagline}</div>
                      <h2 className="text-2xl font-black text-[#0A2463] mt-1">{cat.label}</h2>
                    </div>
                    <span className="text-xs font-bold text-[#0A2463] bg-[#0A2463]/5 px-3 py-1 rounded-full">{cat.slugs.length} services</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {cat.slugs.map((slug) => {
                      const s = services[slug]; if (!s) return null;
                      const Icon = s.icon;
                      return (
                        <Link key={slug} to={`/services/${slug}`} data-testid={`services-page-${slug}`}
                          className="group bg-white rounded-xl border border-gray-100 hover:border-[#0A2463]/30 hover:shadow-lg transition-all p-4 flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#FFB703]/10 flex items-center justify-center flex-shrink-0">
                            <Icon size={18} className="text-[#FB8500]" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-bold text-[#0A2463] leading-tight">{s.title}</div>
                            <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{s.short}</div>
                            <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#FB8500] group-hover:gap-2 transition-all">
                              Explore <ChevronRight size={11} />
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="max-w-3xl mx-auto text-center mt-14">
            <h3 className="text-2xl md:text-3xl font-black text-[#0A2463]">Can't find what you need?</h3>
            <p className="text-gray-600 mt-2">Tell us your business requirement — our expert will guide you.</p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-[#FFB703] to-[#FB8500] text-white">Ask an Advisor <ArrowRight size={14} className="ml-1.5" /></Button>
              </Link>
              <a href="tel:9755085383">
                <Button variant="outline" className="border-[#0A2463] text-[#0A2463]">Call 9755085383</Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
